import { Box, IconClear, IconHelp } from 'braid-design-system';
import React, {
  createRef,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useEffect,
  useState,
} from 'react';

import * as styles from './index.css';

type CalloutPosition = undefined | 'top-left';
export interface Props {
  children?: ReactNode;
  position?: CalloutPosition;
  onToggleOn?: () => void;
  show?: boolean;
}

export const ContextualHelp = ({
  show = false,
  position,
  onToggleOn,
  children,
}: Props) => {
  const [shouldShow, setShouldShow] = useState(show);
  const [wrapperRef, setWrapperRef] = useState(createRef<any>());
  const [iconClearRef] = useState(createRef<any>());

  const handleClickOutside = (event: MouseEvent) => {
    if (!wrapperRef.current?.contains(event.target)) {
      setShouldShow(false);
    }
  };
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode === 9) {
      setShouldShow(false);
    }
  };

  const toggle = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!shouldShow) {
      onToggleOn?.();
    }
    setShouldShow(!shouldShow);
  };

  const hide = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setShouldShow(false);
  };

  const onWrapperRef: any = (node: ReactNode) => {
    if (node !== undefined) {
      const n: unknown = node;
      setWrapperRef(n as SetStateAction<RefObject<any>>);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  });

  const getContainerStyle = (containerPosition: CalloutPosition) => {
    switch (containerPosition) {
      case 'top-left':
        return [styles.containerTopRight];

      default:
        return styles.container;
    }
  };

  return (
    <Box ref={onWrapperRef}>
      <Box
        cursor="pointer"
        className={styles.helpIcon}
        onClick={toggle}
        data-testid="helpicon"
      >
        <IconHelp size="fill" tone="secondary" />
      </Box>

      {shouldShow && (
        <Box
          position="absolute"
          background="surface"
          zIndex="modal"
          right={0}
          boxShadow="medium"
          className={getContainerStyle(position)}
        >
          <Box className={styles.content} data-testid="content">
            {children}
          </Box>
          <Box
            position="absolute"
            cursor="pointer"
            className={styles.exitButton}
            onClick={hide}
            ref={iconClearRef}
          >
            <IconClear size="xsmall" />
          </Box>
        </Box>
      )}
    </Box>
  );
};
